import React, { useEffect, useState } from 'react';
import { MessageCircle, Bot, AlertTriangle } from 'lucide-react';
import api from '../utils/api';

const fmtNum = (n) => {
  if (n >= 1e6) return (n / 1e6).toLocaleString('es-AR', { maximumFractionDigits: 1 }) + ' M';
  if (n >= 1e4) return Math.round(n / 1e3).toLocaleString('es-AR') + ' mil';
  return Number(n || 0).toLocaleString('es-AR');
};

function Meter({ icon: Icon, title, m, unit }) {
  const color = m.blocked ? 'var(--danger)' : m.warning ? '#f59e0b' : 'var(--accent)';
  return (
    <div style={{ minWidth: 190, flex: 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4, gap: 8 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontWeight: 600, color: 'var(--text-secondary)' }}><Icon size={12} /> {title}</span>
        <span style={{ color: m.blocked ? 'var(--danger)' : 'var(--text-muted)' }}>{fmtNum(m.used)} / {fmtNum(m.limit)} {unit}</span>
      </div>
      <div style={{ height: 6, background: 'var(--bg-hover)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: m.pct + '%', height: '100%', background: color, transition: 'width .3s' }} />
      </div>
    </div>
  );
}

// Consumo del mes (WhatsApp en mensajes, Agente IA en tokens). Sin montos.
export default function UsageMeters({ onChange, compact = false }) {
  const [u, setU] = useState(null);
  useEffect(() => {
    const load = () => api.get('/usage').then(r => { setU(r.data); onChange && onChange(r.data); }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!u) return null;

  const alerts = [];
  if (u.whatsapp.blocked) alerts.push({ level: 'blocked', text: 'Llegaste al tope de mensajes de WhatsApp de este mes. El agente y las respuestas por WhatsApp están pausados.' });
  else if (u.whatsapp.warning) alerts.push({ level: 'warning', text: `Usaste el ${u.whatsapp.pct}% de tus mensajes de WhatsApp de este mes.` });
  if (u.ai.blocked) alerts.push({ level: 'blocked', text: 'Llegaste al tope de uso del Agente IA de este mes. El agente dejó de responder; podés seguir respondiendo a mano por Instagram.' });
  else if (u.ai.warning) alerts.push({ level: 'warning', text: `Usaste el ${u.ai.pct}% del Agente IA de este mes.` });

  if (compact) {
    const worst = alerts.find(x => x.level === 'blocked') || alerts[0];
    const Mini = ({ icon: Icon, title, m, unit }) => {
      const color = m.blocked ? 'var(--danger)' : m.warning ? '#f59e0b' : 'var(--accent)';
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-muted)', flex: '1 1 280px', minWidth: 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}><Icon size={13} /> {title}</span>
          <div style={{ flex: 1, minWidth: 50, height: 8, background: 'var(--bg-hover)', borderRadius: 4, overflow: 'hidden' }}><div style={{ width: m.pct + '%', height: '100%', background: color }} /></div>
          <span style={{ whiteSpace: 'nowrap', color: m.blocked ? 'var(--danger)' : m.warning ? '#d97706' : 'var(--text-secondary)' }}>
            <b>{fmtNum(m.used)}</b> / {fmtNum(m.limit)} {unit} · {m.pct}%
          </span>
        </div>
      );
    };
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', padding: '8px 4px 0' }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>Consumo del mes</span>
        <Mini icon={MessageCircle} title="WhatsApp" m={u.whatsapp} unit="mensajes" />
        <Mini icon={Bot} title="Agente IA" m={u.ai} unit="tokens" />
        {worst && <span title={worst.text + ' ' + u.limit_message} style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, color: worst.level === 'blocked' ? 'var(--danger)' : '#d97706' }}>
          <AlertTriangle size={12} /> {worst.level === 'blocked' ? 'Tope alcanzado' : 'Cerca del tope'} · {u.limit_message}
        </span>}
      </div>
    );
  }

  return (
    <div>
      <div className="card" style={{ padding: '10px 14px', display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Consumo del mes</div>
        <Meter icon={MessageCircle} title="WhatsApp" m={u.whatsapp} unit="mensajes" />
        <Meter icon={Bot} title="Agente IA" m={u.ai} unit="tokens" />
      </div>
      {alerts.map((a, i) => (
        <div key={i} style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, fontSize: 13, display: 'flex', gap: 8, alignItems: 'flex-start',
          background: a.level === 'blocked' ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)', color: a.level === 'blocked' ? 'var(--danger)' : '#d97706' }}>
          <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{a.text} {u.limit_message}</span>
        </div>
      ))}
    </div>
  );
}
